import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import {
  StripeCardElementChangeEvent,
  StripeCardElementOptions,
  StripeElementsOptions
} from '@stripe/stripe-js';
import { DeviceDetectorService } from 'ngx-device-detector';
import { StripeCardComponent, StripeService } from 'ngx-stripe';
import { ApiResponseErrorCodes } from 'src/app/core/models/common/enums/api-response-error-codes.enum';
import ErrorResponse from 'src/app/core/models/common/error-response.model';
import Purchase from 'src/app/core/models/purchase.model';
import { AuthService } from 'src/app/core/service/authentication/auth.service';
import { LoaderService } from 'src/app/core/service/loader/loader.service';
import { NotificationService } from 'src/app/core/service/notifications/notification.service';
import { TicketService } from 'src/app/core/service/tickets/tickets.service';
import { environment } from 'src/environments/environment';
import Web3 from 'web3';

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrls: ['./checkout.component.scss'],
})
export class CheckoutComponent implements OnInit {
  @Input() selectedNumbers: number[] = [];
  @Input() votedTicketNumbers: number[] = [];
  @Input() purchaseCostInUsd = 0;
  @Output() confirm = new EventEmitter<unknown>();

  isWalletConnected = false;
  buyerWalletAddress = '';
  isTryMode: boolean = environment.IS_TRY_MODE;

  @ViewChild(StripeCardComponent) cardElement!: StripeCardComponent;

  cardOptions: StripeCardElementOptions = {
    hidePostalCode: true,
    style: {
      base: {
        iconColor: '#666EE8',
        color: '#ffffff',
        fontWeight: '300',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSize: '14px',
        '::placeholder': {
          color: '#CFD7E0',
        },
      },
    },
  };

  elementsOptions: StripeElementsOptions = {
    locale: 'en',
  };

  isCardValid = false;
  isMobile = false;

  paymentTab = 'card';
  selectedPaymentMethod = 'card';
  purchaseCostInEth = 0;
  purchaseSellerWallet = '';

  checkoutCardForm: FormGroup = new FormGroup({
    cardName: new FormControl('', [Validators.required]),
  });

  checkoutWalletForm: FormGroup = new FormGroup({});

  previousIncomingWallet: string | undefined;
  previousOutcomitWallet: string | undefined;

  constructor(
    private ticketService: TicketService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private notificationService: NotificationService,
    public stripe: StripeService,
    private router: Router,
    private authService: AuthService,
    private deviceDetectorService: DeviceDetectorService
  ) {}

  ngOnInit(): void {
    this.ticketService.playPeriod$.subscribe((playPeriod) => {
      this.purchaseCostInUsd = playPeriod?.purchaseCostInUsd ?? 0;
      this.purchaseCostInEth = playPeriod?.purchaseCostInEth ?? 0;
      this.purchaseSellerWallet = playPeriod?.purchaseSellerWallet ?? '';
    });

    this.authService.currentUser$.subscribe((user) => {
      this.previousIncomingWallet = user?.incomingWalletHash;
      this.previousOutcomitWallet = user?.outcomingWalletHash;

      this.checkoutWalletForm = new FormGroup({
        walletInbTrans: new FormControl(user?.incomingWalletHash, {
          validators: [Validators.required],
        }),
        walletOutTrans: new FormControl(user?.outcomingWalletHash, {
          validators: [Validators.required],
        }),
      });
    });
    this.checkIfMobile();
    this.checkWallet();
  }

  setActivePayment(type: string): void {
    this.paymentTab = type;
  }

  isPaymentActive(type: string): boolean {
    return this.paymentTab === type;
  }

  private checkIfMobile(): void {
    this.isMobile = this.deviceDetectorService.isMobile();
    this.cdr.detectChanges();
  }

  private formatNumbers(numbers: any[]): string {
    return numbers
      .map((num) => (parseInt(num) >= 1 && parseInt(num) <= 9 ? `0${parseInt(num)}` : `${num}`)) // Aggiunge lo zero solo ai numeri da 1 a 9
      .join('-');
  }

  async onCheckoutCardSubmit() {
    this.loaderService.showLoader();
    this.ticketService
      .postTicketByCard(
        this.formatNumbers(this.selectedNumbers),
        this.formatNumbers(this.votedTicketNumbers)
      )
      .subscribe(async (response) => {
        try {
          this.stripe
            .confirmCardPayment((response.content as Purchase).clientSecret, {
              payment_method: {
                card: this.cardElement.element,
                billing_details: {
                  name: this.checkoutCardForm.value.cardName,
                },
              },
            })
            .subscribe((payment) => {
              console.log(payment);
              if (payment.paymentIntent?.status === 'succeeded') {
                this.router.navigateByUrl('purchase-confirmed');
              }
            });
        } catch (error) {
          this.notificationService.showError(
            'Purchase not completed, please retry or contact the support.'
          );
          console.error('Payment error:', error);
        } finally {
          this.cdr.detectChanges();
          this.loaderService.hideLoader();
        }
      });
  }

  async onCheckoutPaypalSubmit() {
    this.loaderService.showLoader();
    this.ticketService
      .postTicketByPaypal(
        this.formatNumbers(this.selectedNumbers),
        this.formatNumbers(this.votedTicketNumbers)
      )
      .subscribe(async (response) => {
        try {
          this.stripe
            .confirmPayPalPayment((response.content as Purchase).clientSecret, {
              payment_method: {
                billing_details: {
                  email: this.authService.currentUser$.value?.emailAddress,
                },
              },
              return_url: window.location.origin + '/purchase-confirmed',
            })
            .subscribe((payment) => {
              console.log(payment);
              if (payment.paymentIntent?.status === 'succeeded') {
                this.router.navigateByUrl('purchase-confirmed');
              }
            });
        } catch (error) {
          this.notificationService.showError(
            'Purchase not completed, please retry or contact the support.'
          );
          console.error('Payment error:', error);
        } finally {
          this.cdr.detectChanges();
          this.loaderService.hideLoader();
        }
      });
  }

  onCardChange(event: StripeCardElementChangeEvent): void {
    if (event.complete) this.isCardValid = event.complete;
  }

  async checkWallet(): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-const
    let currentWindow = window as any;

    /* Check if the user has a wallet installed */

    if (!currentWindow.ethereum) {
      this.isWalletConnected = false;

      return;
    }

    /* Check if the wallet is connected */

    const accounts = await currentWindow.ethereum.request({
      method: 'eth_accounts',
    });

    if (accounts.length) {
      this.buyerWalletAddress = accounts[0];

      this.isWalletConnected = true;
    } else {
      this.isWalletConnected = false;
    }
  }

  async onConnectWallet(): Promise<void> {
    this.loaderService.showLoader();

    let currentWindow = window as any;

    if (currentWindow.ethereum) {
      currentWindow.web3 = new Web3(currentWindow.ethereum);

      try {
        await currentWindow.ethereum.send('eth_requestAccounts');
        const accounts = await currentWindow.ethereum.request({
          method: 'eth_accounts',
        });

        if (accounts.length) {
          this.buyerWalletAddress = accounts[0];

          this.isWalletConnected = true;
        }
      } catch {
        this.notificationService.showError(
          'There was an error during the connection to the wallet. Please retry or contact the support.'
        );
      }
    } else if (currentWindow.web3)
      currentWindow.web3 = new Web3(currentWindow.web3.currentProvider);
    else {
      this.isWalletConnected = true;
    }

    this.loaderService.hideLoader();
  }

  private sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  private async getTransactionDetails(transactionHash: string) {
    return await (window as any).ethereum.request({
      method: 'eth_getTransactionByHash',
      params: [transactionHash],
    });
  }

  async onPayWithWallet(): Promise<void> {
    this.loaderService.showLoader();

    console.log(this.previousIncomingWallet);
    console.log(this.checkoutWalletForm.value);

    if (
      this.previousIncomingWallet !==
        this.checkoutWalletForm.value.walletInbTrans ||
      this.previousOutcomitWallet !==
        this.checkoutWalletForm.value.walletOutTrans
    ) {
      this.authService
        .updateWallet(
          this.checkoutWalletForm.value.walletInbTrans,
          this.checkoutWalletForm.value.walletOutTrans
        )
        .subscribe(async (response) => {
          if (response) {
            const errorResponse = response.content as ErrorResponse;

            if (
              errorResponse.type ===
              ApiResponseErrorCodes.AlreadyExistsUserWithSameWallet
            )
              this.notificationService.showError(
                'One of your wallets has already taken by another user. Please, choose another one.'
              );
          } else {
            this.notificationService.showSuccess(
              'Your wallets have been updated'
            );

            await this.proceedToWalletPayment();
          }
        });
    } else this.proceedToWalletPayment();
  }

  private async proceedToWalletPayment(): Promise<void> {
    const currentWindow = window as any;
    /* Send the transaction */

    try {
      const purchaseCostInEthFormatted = this.purchaseCostInEth.toLocaleString(
        'en-US',
        {
          useGrouping: false,
          maximumFractionDigits: 20,
        }
      );

      const costInWei = Web3.utils.toWei(purchaseCostInEthFormatted, 'ether');

      const transactionHash = await currentWindow.ethereum.request({
        method: 'eth_sendTransaction',
        params: [
          {
            from: this.buyerWalletAddress,
            to: this.purchaseSellerWallet,
            value: '0x' + parseInt(costInWei).toString(16),
          },
        ],
      });

      console.log('Transaction confirmed with hash ' + transactionHash);

      if (transactionHash) {
        /* Wait for the transaction's completition */

        let transactionDetails;

        do {
          await this.sleep(2500);

          transactionDetails = await this.getTransactionDetails(
            transactionHash
          );

          console.log(
            'Checking for completition of transaction with hash ' +
              transactionHash
          );
        } while (!transactionDetails?.blockHash);

        /* Send the purchase to the server */

        this.ticketService
          .postTicketByCrypto(
            this.formatNumbers(this.selectedNumbers),
            this.formatNumbers(this.votedTicketNumbers),
            transactionHash
          )
          .subscribe(() => {
            this.router.navigateByUrl('purchase-confirmed');
          });
      }
    } catch {
      this.loaderService.hideLoader();

      this.notificationService.showError(
        'Purchase not completed, please retry or contact the support.'
      );
    }
  }
}
